/**
 * Усі тексти інтерфейсу. Підписи доменних значень (етапи, статуси, ролі) — у @hr/shared,
 * бо їх використовує і серверний експорт; тут вони реекспортуються.
 */
import { ukWsdms } from './uk-wsdms';

export {
  ABSENCE_LABELS,
  ABSENCE_SHORT,
  ACT_STATUS_LABELS,
  COMMITTEE_ROLE_LABELS,
  CONTEST_STATUS_LABELS,
  CONTRACT_KIND_LABELS,
  CONTRACT_STATUS_LABELS,
  DAY_KIND_LABELS,
  DOCUMENT_KIND_LABELS,
  DUPLICATE_REASON_LABELS,
  EMPLOYEE_STATUS_LABELS,
  KATOTTG_CATEGORY_LABELS,
  OCCUPANCY_LABELS,
  PROJECT_STATUS_LABELS,
  RATE_UNIT_LABELS,
  READY_FOR_FOP_LABELS,
  REGISTER_STATUS_LABELS,
  REPORT_STATUS_LABELS,
  ROLE_LABELS,
  SERVICE_UNIT_LABELS,
  SOURCE_KIND_LABELS,
  STAFF_POSITION_STATUS_LABELS,
  FUNNEL_LEVEL_LABELS,
  STAGE_KIND_HINTS,
  STAGE_KIND_LABELS,
  TEMPLATE_KIND_LABELS,
  TIMESHEET_STATUS_LABELS,
} from '@hr/shared';

export const uk = {
  ...ukWsdms,
  app: {
    name: 'WSDMS',
    tagline: 'Управління персоналом і наданням послуг',
  },
  nav: {
    contests: 'Конкурси',
    candidates: 'Кандидати',
    reminders: 'Нагадування',
    duplicates: 'Можливі дублікати',
    reference: 'Довідники',
    users: 'Користувачі',
    projects: 'Проєкти',
    staffing: 'Штатний розклад',
    groupRecruitment: 'Відбір',
    groupRegistry: 'Реєстр посад',
    groupAdmin: 'Адміністрування',
    groupPersonnel: 'Персонал',
    groupTime: 'Облік часу',
    calendar: 'Календар',
    timesheets: 'Табелі',
    groupCabinet: 'Кабінет',
    myTimesheet: 'Мій табель',
    myReports: 'Мої звіти',
    groupServices: 'Надання послуг',
    groupFinance: 'Фінанси',
    acts: 'Акти',
    payroll: 'Реєстри винагород',
    groupReports: 'Звітність',
    analytics: 'Аналітика',
    documents: 'Архів документів',
    reports: 'Звіти про послуги',
    employees: 'Працівники',
    contracts: 'Договори',
    search: 'Пошук…',
    searchHint: 'Ctrl K',
    searchDescription: 'Пошук кандидатів, конкурсів і телефонів',
    logout: 'Вийти',
    menu: 'Меню',
  },
  login: {
    title: 'Вхід',
    subtitle: 'Увійдіть, щоб працювати з конкурсами і кандидатами',
    email: 'Email',
    password: 'Пароль',
    submit: 'Увійти',
    submitting: 'Входимо…',
    failed: 'Не вдалося увійти',
  },
  common: {
    save: 'Зберегти',
    saving: 'Збереження…',
    cancel: 'Скасувати',
    close: 'Закрити',
    create: 'Створити',
    add: 'Додати',
    edit: 'Редагувати',
    delete: 'Видалити',
    archive: 'Архівувати',
    unarchive: 'Повернути з архіву',
    undo: 'Скасувати',
    retry: 'Спробувати ще раз',
    loading: 'Завантаження…',
    notFound: 'Нічого не знайдено',
    empty: '—',
    yes: 'Так',
    no: 'Ні',
    all: 'Усі',
    search: 'Пошук',
    actions: 'Дії',
    saved: 'Збережено',
    error: 'Сталася помилка',
    forbidden: 'Недостатньо прав',
    conflict: 'Запис щойно змінив інший користувач. Дані оновлено — перевірте й повторіть.',
    rowsPerPage: 'Рядків на сторінці',
    pageOf: (page: number, pages: number) => `Сторінка ${page} з ${Math.max(pages, 1)}`,
    total: (n: number) => `Усього: ${n}`,
    prevPage: 'Попередня сторінка',
    nextPage: 'Наступна сторінка',
    pickDate: 'Оберіть дату',
    datePlaceholder: 'дд.мм.рррр',
    invalidDate: 'Некоректна дата',
    clear: 'Очистити',
  },
  users: {
    title: 'Користувачі',
    add: 'Новий користувач',
    email: 'Email',
    fullName: "Повне ім'я",
    role: 'Роль',
    password: 'Пароль',
    newPassword: 'Новий пароль',
    passwordHint: 'Залиште порожнім, щоб не змінювати',
    active: 'Активний',
    inactive: 'Вимкнений',
    locked: 'Заблоковано до',
    deactivate: 'Вимкнути',
    activate: 'Увімкнути',
    created: 'Користувача створено',
    passwordOptional: 'Залиште порожнім — людина отримає лист і задасть пароль сама',
    invited: 'Запрошено',
    invite: 'Надіслати запрошення',
    you: 'це ви',
    searchPlaceholder: 'Пошук за ім’ям або email…',
  },
  mail: {
    title: 'Пошта',
    folders: 'Папки',
    roles: {
      inbox: 'Вхідні',
      sent: 'Надіслані',
      drafts: 'Чернетки',
      trash: 'Кошик',
      junk: 'Спам',
      archive: 'Архів',
    } as Record<string, string>,
    connectHint:
      'Підключіть свою корпоративну скриньку: введіть адресу і пароль. Якщо скриньки немає — зверніться до HR.',
    needPassword: 'Пароль скриньки змінився або ще не збережений — введіть поточний пароль.',
    blocked: 'Доступ до скриньки закрито. Зверніться до HR.',
    address: 'Адреса скриньки',
    password: 'Пароль скриньки',
    connect: 'Підключити',
    connected: 'Скриньку підключено',
    compose: 'Новий лист',
    refresh: 'Оновити',
    searchPlaceholder: 'Пошук у листах…',
    empty: 'Листів немає',
    pick: 'Оберіть розмову',
    conversations: 'Розмови',
    resizeList: 'Ширина списку розмов',
    resizeListHint: 'Потягніть, щоб змінити ширину; подвійний клік — типова ширина',
    allMail: 'Усі листи',
    more: 'Показати ще',
    today: 'Сьогодні',
    yesterday: 'Вчора',
    onlyMe: 'Лише ви',
    count: 'Листів у розмові',
    you: 'Ви',
    youLast: 'Ви відповіли останнім',
    lastFrom: (who: string) => `Останній: ${who}`,
    members: (who: string, n: number) => `${who} · листів: ${n}`,
    messageFrom: (who: string) => `Лист від ${who}`,
    replyToAbove: (who: string) => `Відповідь на лист вище (${who})`,
    replyToFrom: (who: string, date: string) => `Відповідь на лист: ${who}, ${date}`,
    goToParent: 'Показати лист, на який відповіли',
    showQuote: 'Показати цитату',
    hideQuote: 'Сховати цитату',
    history: (n: number) => `Попереднє листування · ${n}`,
    showOriginal: 'Показати лист так, як його надіслали',
    hideOriginal: 'Сховати оригінал',
    replyingTo: (who: string) => `Відповідь на лист: ${who}`,
    replyLast: 'Відповідати, як типово',
    changeRecipients: 'змінити',
    replyPlaceholder: 'Напишіть відповідь…',
    sendHotkey: 'Ctrl+Enter — надіслати',
    threadToTrash: 'Розмову в кошик',
    threadDelete: 'Видалити розмову назавжди',
    deleteForever: 'Видалити назавжди',
    deleteForeverHint: (n: number) =>
      n > 1
        ? `Листів: ${n}. Їх буде видалено з поштового сервера остаточно — відновити не вийде.`
        : 'Лист буде видалено з поштового сервера остаточно — відновити не вийде.',
    deleted: (n: number) => (n > 1 ? `Видалено назавжди листів: ${n}` : 'Лист видалено назавжди'),
    preview: 'Переглянути',
    download: 'Завантажити',
    signature: 'Підпис',
    signatureTitle: 'Підпис листів',
    signatureHint:
      'Додається в кінці ваших листів і відповідей. Можна вказати посаду, телефон, посилання на сайт і додати фото чи логотип.',
    signaturePlaceholder: 'З повагою, Ім’я Прізвище, посада, телефон…',
    signatureImage: 'Додати фото / логотип',
    signatureImageReplace: 'Замінити картинку',
    signatureImageRemove: 'Прибрати картинку',
    signatureImageHint: 'PNG, JPEG або GIF до 512 КБ',
    signatureImageTooBig: 'Картинка більша за 512 КБ',
    signatureLeft: 'Ліворуч від тексту',
    signatureBelow: 'Під текстом',
    signatureWidth: 'Ширина, px',
    signaturePreview: 'Так підпис виглядатиме в листі',
    signatureEnabled: 'За замовчуванням для цієї скриньки',
    signatureNew: 'Новий підпис',
    signatureName: 'Назва',
    signatureNone: 'Без підпису',
    signatureDefaultMark: 'типовий',
    signatureAccount: 'Скринька',
    signatureList: 'Підписи скриньки',
    signatureEmpty: 'У цієї скриньки ще немає підписів.',
    signatureDelete: 'Видалити підпис',
    signatureDeleteConfirm: 'Точно видалити?',
    signatureDeleted: 'Підпис видалено',
    signatureSaved: 'Підпис збережено',
    signatureAdd: 'Підпис',
    signatureSetup: 'Налаштувати підпис',
    forwardedBelow: 'Пересланий лист буде додано нижче:',
    quoteBelow: 'Цитата листа, на який відповідаєте (додасться нижче):',
    draftLabel: 'Чернетка',
    showHistory: 'Показати попереднє листування',
    hideHistory: 'Сховати попереднє листування',
    removeRecipient: 'Прибрати',
    clearRecipients: 'Прибрати всіх',
    recipientsCleared: (n: number) => `Прибрано адресатів: ${n}`,
    doneRecipients: 'Готово',
    replySender: 'Відправнику',
    replyEveryone: (n: number) => `Усім (${n})`,
    replyCustom: 'Свій список',
    noRecipients: 'Немає адресатів',
    invalidAddress: 'Схоже, адреса з помилкою — лист на неї не піде.',
    notInContacts: 'Цієї адреси немає в контактах.',
    moreRecipients: (n: number) => `і ще ${n}`,
    showLess: 'згорнути',
    showAllRecipients: (n: number) => `показати всіх (${n})`,
    collapseReply: 'Згорнути',
    draftCollapsed: (text: string) => `Чернетка: ${text}`,
    draftSaving: 'Зберігаємо чернетку…',
    draftSaved: (time: string) => `Чернетку збережено о ${time}`,
    draftError: 'Чернетку не збережено',
    draftNoFiles: 'Вкладення в чернетці не зберігаються — додайте їх перед надсиланням.',
    discardDraft: 'Видалити чернетку',
    draftDeleted: 'Чернетку видалено',
    draftOpenInWindow:
      'Ця чернетка відкрита у вікні листа — видаліть її кнопкою з кошиком у вікні.',
    closeDraft: 'Закрити (чернетка збережеться)',
    minimize: 'Згорнути',
    expand: 'Розгорнути',
    openInWindow: 'Відкрити у вікні',
    draftOpenFailed: 'Не вдалося відкрити чернетку',
    threadTrashed: 'Розмову перенесено в кошик',
    notFound: 'Лист не знайдено',
    noSubject: '(без теми)',
    newer: 'Новіші',
    older: 'Старіші',
    back: 'Назад',
    reply: 'Відповісти',
    replyAll: 'Відповісти всім',
    forward: 'Переслати',
    markUnread: 'Непрочитаний',
    toTrash: 'У кошик',
    trashed: 'Лист перенесено в кошик',
    from: 'Від',
    to: 'Кому',
    toHint: 'Кілька адрес — через кому',
    cc: 'Копія',
    bcc: 'Прихована',
    bccHint: 'Прихована копія: інші адресати не бачать, що лист отримали ці люди.',
    date: 'Дата',
    subject: 'Тема',
    bodyPlaceholder: 'Текст листа…',
    attach: 'Додати файл',
    tooBig: (name: string, mb: number) => `${name}: більше ${mb} МБ`,
    send: 'Надіслати',
    sending: 'Надсилаємо…',
    sent: 'Лист надіслано',
    sendFailed: 'Лист не надіслано',
    wrote: (date: string, who: string) => `${date}, ${who} пише:`,
    forwarded: (header: string) => `Пересланий лист. ${header}`,
    nav: 'Пошта',
    filters: { all: 'Усі', unread: 'Непрочитані', flagged: 'Важливі' },
    searchOptions: {
      open: 'Налаштування пошуку',
      fieldsLabel: 'Шукати текст у',
      fields: {
        subject: 'Тема',
        body: 'Текст листа',
        from: 'Від кого',
        to: 'Кому',
        cc: 'Копія',
        bcc: 'Прихована копія',
      },
      fieldsHint: 'Хоча б одне поле',
      typeLabel: 'Тип листа',
      types: {
        any: 'Усі листи',
        unread: 'Непрочитані',
        read: 'Прочитані',
        flagged: 'Важливі',
        answered: 'З відповіддю',
        unanswered: 'Без відповіді',
      },
      periodLabel: 'Період',
      presets: { today: 'Сьогодні', week: '7 днів', month: '30 днів', year: 'Рік' },
      after: 'Від',
      before: 'До',
      whereLabel: 'Де шукати',
      here: 'У вибраній папці',
      everywhere: 'В усіх папках',
      attachments: 'З вкладеннями',
      sizeLabel: 'Розмір',
      anySize: 'Будь-який',
      larger: (mb: number) => `Більше ${mb} МБ`,
      sortLabel: 'Порядок',
      sort: { newest: 'Спочатку нові', oldest: 'Спочатку старі' },
      reset: 'Скинути',
      done: 'Готово',
      remove: 'Прибрати умову',
      chipFields: (names: string) => `У полях: ${names}`,
      chipAfter: (d: string) => `від ${d}`,
      chipBefore: (d: string) => `до ${d}`,
      chipPeriod: (from: string, to: string) => `${from} – ${to}`,
      periodInvalid: '«Від» пізніше, ніж «До», — нічого не знайдеться',
      sortHint: 'Порядок діє, коли щось шукаєте',
    },
    emptySearch: 'Нічого не знайдено',
    accountDialog: {
      title: 'Додати скриньку',
      description:
        'Будь-яка пошта: Gmail, ukr.net, i.ua, корпоративна іншої організації… Листи лишаються на її сервері, тут — лише показуються.',
      address: 'Адреса',
      password: 'Пароль',
      detected: (name: string) => `Розпізнано: ${name}`,
      appPassword: (name: string) =>
        `${name} пускає поштові програми лише з паролем застосунку: увімкніть двоетапну перевірку і створіть «пароль застосунку» в налаштуваннях безпеки облікового запису. Звичайний пароль не підійде.`,
      servers: 'Сервери',
      serversAuto: 'визначаться за адресою',
      host: 'сервер',
      port: 'порт',
      login: 'Логін (якщо не збігається з адресою)',
      loginPlaceholder: 'як адреса',
      connect: 'Підключити',
      checking: 'Перевіряємо вхід…',
      connected: (address: string) => `Скриньку ${address} підключено`,
      forUserDescription:
        'Скринька з’явиться в поштовику працівника. Пароль він не побачить і відключити скриньку сам не зможе. Зручно для спільних hr@, support@.',
    },
    accounts: {
      add: 'Додати скриньку',
      addOther: 'Підключити іншу пошту (Gmail, ukr.net…)',
      remove: 'Відключити скриньку…',
      removeTitle: (address: string) => `Відключити ${address}?`,
      removeText:
        'Скринька зникне з поштовика. Листи на її сервері лишаються — підключити знову можна будь-коли.',
      removed: 'Скриньку відключено',
      menu: (address: string) => `Дії зі скринькою ${address}`,
      assigned: 'Підключив адміністратор',
      newFolder: 'Нова папка',
    },
    folderPanel: {
      label: 'Папки',
      open: 'Папки',
      all: 'Усі листи',
      create: 'Нова папка',
      createNested: 'Нова вкладена папка',
      edit: 'Налаштувати',
      delete: 'Видалити…',
      actions: (name: string) => `Дії з папкою «${name}»`,
      collapse: 'Згорнути панель папок',
      expand: 'Розгорнути панель папок',
      collapseBranch: 'Згорнути',
      expandBranch: 'Розгорнути',
      resize: 'Ширина панелі папок',
      resizeHint: 'Потягніть, щоб змінити ширину; подвійний клік — типова ширина',
    },
    folderDialog: {
      createTitle: 'Нова папка',
      editTitle: 'Папка',
      description:
        'Папка створюється на поштовому сервері. Листи в неї додаєте самі (меню «Перемістити в папку») або налаштовуєте правила.',
      rulesToggle: 'Правила для нових листів',
      rulesOptional: 'необов’язково',
      keep: 'Лишати листи у «Вхідних» — лише додавати в папку',
      keepHint:
        'Як у Telegram: лист видно і у «Вхідних», і в папці. Без цього — переноситься в папку (і на телефоні він теж у папці).',
      name: 'Назва',
      namePlaceholder: 'Наприклад, Вакансії',
      look: 'Вигляд',
      color: 'Колір',
      noColor: 'Без кольору',
      customColor: 'Свій колір',
      autoColor: 'Авто (контрастний до бульбашки)',
      paintTo: 'Колір для:',
      paintAll: 'усе',
      paintNone: 'нічого',
      paintSeparate: 'Різні кольори для кожного',
      paintParts: { bubble: 'Бульбашка', text: 'Текст', icon: 'Іконка' },
      icon: 'Іконка',
      parent: 'Розташування',
      topLevel: 'Окрема папка (верхній рівень)',
      inside: (label: string) => `Усередині «${label}»`,
      rulesLabel: 'Переносити сюди нові листи, якщо…',
      rulesHint: 'Без умов — переносите вручну.',
      match: { any: 'Підходить хоч одна умова', all: 'Підходять усі умови' },
      kinds: {
        from: 'Від кого',
        sender: 'Відправник містить',
        domain: 'Домен відправника',
        subject: 'У темі є',
        body: 'У тексті є',
        to: 'Кому або копія',
      },
      placeholders: {
        from: 'адреса@proliska.org',
        sender: 'частина імені чи адреси',
        domain: 'proliska.org',
        subject: 'слово чи фраза',
        body: 'слово чи фраза',
        to: 'спільна@proliska.org',
      },
      attachments: 'З вкладеннями',
      lists: 'Розсилки й сповіщення',
      listsHint: 'Листи з посиланням «Відписатися» (List-Unsubscribe) — новини, сервіси, Google',
      sizeLabel: 'Розмір',
      anySize: 'Будь-який',
      larger: (mb: number) => `Більше ${mb} МБ`,
      exceptLabel: 'Крім листів, де в темі чи тексті є…',
      exceptPlaceholder: 'наприклад, терміново',
      exceptHint: 'Такі листи лишаються у «Вхідних», навіть якщо підходять умови.',
      addRule: 'Додати',
      removeRule: 'Прибрати правило',
      noRules: 'Правил немає',
      nameTaken: 'Тут уже є папка з такою назвою',
      invalidEmail: 'Некоректна адреса',
      invalidDomain: 'Некоректний домен',
      applyExisting: 'Також перенести листи з «Вхідних», що вже підходять',
      create: 'Створити',
      save: 'Зберегти',
      created: (moved: number) =>
        moved ? `Папку створено, перенесено листів: ${moved}` : 'Папку створено',
      existed: (moved: number) =>
        moved
          ? `Така папка вже була на сервері — відкрито її, перенесено листів: ${moved}`
          : 'Така папка вже була на сервері — відкрито її',
      saved: (moved: number) => (moved ? `Збережено, перенесено листів: ${moved}` : 'Збережено'),
      delete: 'Видалити папку',
      deleteTitle: (name: string) => `Видалити папку «${name}»?`,
      deleteText:
        'Папку буде видалено з поштового сервера, а її листи повернуться у «Вхідні». Правила папки зникнуть.',
      deleted: (moved: number) =>
        moved ? `Папку видалено, листів повернуто у «Вхідні»: ${moved}` : 'Папку видалено',
    },
    move: {
      button: 'Перемістити в папку',
      moveTo: 'Перенести в папку',
      linkTo: 'Додати, лишивши на місці',
      linkHint: 'Лист лишиться, де був, і буде видно також у папці',
      linked: (name: string) => `Додано в «${name}»`,
      remove: 'Прибрати з цієї папки',
      removed: 'Прибрано з папки',
      toInbox: 'Повернути у «Вхідні»',
      newFolder: 'Нова папка…',
      moved: (name: string) => `Переміщено в «${name}»`,
      always: (who: string) => `Завжди від ${who}`,
      alwaysDone: (who: string, name: string) => `Листи від ${who} надалі йтимуть у «${name}»`,
      nothing: 'Ваші листи лишаються в «Надісланих» — переміщувати нічого',
    },
    emptyFiltered: { unread: 'Непрочитаних немає', flagged: 'Важливих листів немає' },
    flag: 'Позначити важливим',
    unflag: 'Зняти позначку «важливе»',
    select: 'Вибрати',
    selectAll: 'Вибрати всі',
    selected: (n: number) => `Вибрано: ${n}`,
    clearSelection: 'Зняти вибір',
    markRead: 'Прочитано',
    markUnreadShort: 'Непрочитано',
    markAllRead: 'Усе прочитано',
    allReadDone: 'Усі листи позначено прочитаними',
    trashedMany: (n: number) => `Розмов у кошику: ${n}`,
    newMessages: 'Нові листи',
    dropFiles: 'Відпустіть, щоб додати файли до листа',
    shortcuts: 'Клавіатурні скорочення',
    shortcutsHint: 'Працюють на сторінці «Пошта», коли курсор не в полі вводу.',
    shortcutList: [
      ['j / k', 'Наступна / попередня розмова'],
      ['Esc', 'Закрити розмову'],
      ['r', 'Відповісти'],
      ['a', 'Відповісти всім'],
      ['f', 'Переслати'],
      ['s', 'Важливе'],
      ['Shift + U', 'Позначити непрочитаним'],
      ['#  або  Delete', 'У кошик'],
      ['c', 'Новий лист'],
      ['/', 'Пошук'],
      ['Ctrl + Enter', 'Надіслати'],
      ['?', 'Ця підказка'],
    ] as [string, string][],
  },
  mailbox: {
    tab: 'Пошта',
    extras: 'Додаткові скриньки',
    extrasNone: 'Немає. Можна підключити спільну (hr@, support@) чи будь-яку іншу пошту.',
    extraAdd: 'Підключити скриньку',
    extraAssigned: 'підключив HR',
    extraOwn: 'підключив сам',
    title: 'Корпоративна пошта',
    none: 'Скриньку не підключено.',
    created: 'створено з сайту',
    connected: 'підключено',
    blocked: (at: string) => `доступ закрито ${at}`,
    create: 'Створити скриньку',
    connect: 'Підключити наявну',
    resetPassword: 'Новий пароль',
    unblock: 'Відновити доступ (новий пароль)',
    resetConfirm: (a: string) =>
      `Для ${a} буде згенеровано новий пароль на хостингу. Старий пароль перестане діяти — на телефоні й у поштових програмах людині треба буде ввести новий.`,
    unlink: 'Відв’язати',
    unlinkConfirm: (a: string) =>
      `${a} буде відв’язано від користувача на сайті. Сама скринька й листи на хостингу лишаються без змін.`,
    unlinked: 'Скриньку відв’язано',
    passwordOnce:
      'Пароль показується один раз. Передайте його людині або збережіть — він також збережений на сайті для поштовика.',
    createTitle: 'Нова скринька в корпоративній пошті',
    createHint: 'Оберіть вільну адресу або введіть свою. Пароль згенерується автоматично.',
    taken: 'зайнято',
    ownLogin: 'Логін',
    creating: 'Створюємо…',
    createSubmit: 'Створити',
    connectTitle: 'Підключити наявну скриньку',
    connectHint:
      'Для поштовика на сайті потрібен пароль скриньки: введіть поточний або згенеруйте новий.',
    address: 'Адреса',
    currentPassword: 'Поточний пароль скриньки',
    resetInstead: 'Згенерувати новий пароль через хостинг',
    resetWarning: 'Старий пароль перестане діяти на всіх пристроях людини.',
    connecting: 'Підключаємо…',
    connectSubmit: 'Підключити',
    connectedToast: (a: string) => `Скриньку ${a} підключено`,
    needAccount:
      'Спершу створіть доступ до кабінету — скринька прив’язується до облікового запису.',
    dismissTitle: 'Корпоративна скринька',
    dismissKeep: 'Лишити як є',
    dismissBlock: 'Закрити доступ: змінити пароль (листи збережуться)',
    dismissDelete: 'Видалити скриньку разом з усіма листами',
    dismissDeleteConfirm: 'Розумію, що видалення незворотне',
    dismissFailed: (e: string) => `Працівника звільнено, але зі скринькою не вдалося: ${e}`,
  },
  email: {
    write: 'Написати лист',
    title: 'Новий лист',
    fromSystem: 'Лист піде із системної скриньки; відповідь прийде на вашу адресу.',
    fromPersonal: (a: string) => `Лист піде з вашої скриньки ${a}; копія — у «Надісланих».`,
    notConfigured: 'Пошту не налаштовано (Довідники → Пошта) — лист не буде надіслано.',
    template: 'Шаблон',
    noTemplate: 'Без шаблону',
    to: 'Кому',
    subject: 'Тема',
    body: 'Текст листа',
    bodyPlaceholder: 'Текст листа…',
    effectHint: 'Після відправлення в участі автоматично стане дата листа.',
    send: 'Надіслати',
    sending: 'Надсилаємо…',
    sent: (to: string) => `Лист надіслано на ${to}`,
    failed: (error: string) => `Лист не надіслано: ${error}`,
    history: 'Листи',
    noHistory: 'Листів із системи ще не надсилали.',
    correspondence: 'Листування у вашій скриньці',
    noCorrespondence: 'Листів з цією адресою у вашій скриньці немає.',
    notDelivered: 'не надіслано',
    winner: 'Лист переможцю',
    rejection: 'Лист з відмовою',
    interview: 'Запросити на співбесіду',
  },
  emailTemplates: {
    tab: 'Шаблони листів',
    hint: 'Тексти листів, які надсилаються з карток кандидатів, участі в конкурсі та працівників, а також лист-запрошення в систему. Змінні у {{подвійних дужках}} підставляються автоматично.',
    add: 'Новий шаблон',
    system: 'системний',
    forContext: (label: string) => `Для адресата: ${label}`,
    name: 'Назва',
    context: 'Адресат',
    contextLocked: 'Для системного шаблону не змінюється',
    subject: 'Тема',
    body: 'Текст',
    bodyPlaceholder: 'Текст листа…',
    variables: 'Змінні',
    variablesHint: 'Клацніть, щоб скопіювати, і вставте в тему чи текст.',
    copyVar: 'Скопіювати',
    copied: (v: string) => `Скопійовано ${v}`,
    preview: 'Перегляд',
    edit: 'Редагувати',
    unknown: (list: string) => `Невідомі змінні: ${list}`,
  },
  mailSettings: {
    tab: 'Пошта',
    hint: 'Налаштування пошти для всієї системи. Зміни бачить лише адміністратор; паролі й токен зберігаються зашифрованими і після збереження не показуються.',
    noSecretKey:
      'На сервері не задано APP_SECRET — паролі й токен зберегти не вийде. Додайте APP_SECRET у .env сервера (довгий випадковий рядок).',
    server: 'Поштовий сервер',
    serverHint:
      'Один сервер для надсилання (SMTP) і читання пошти (IMAP) — для системної скриньки й поштовика кожного користувача. Порожній сервер — діють змінні SMTP_* з .env сервера.',
    host: 'Адреса сервера',
    smtpPort: 'Порт надсилання (SMTP)',
    imapPort: 'Порт читання (IMAP)',
    systemMailbox: 'Системна скринька',
    systemHint: 'З цієї адреси йдуть листи-запрошення в систему й інші автоматичні листи.',
    source: {
      settings: 'з довідника',
      env: 'з .env сервера',
      none: 'не налаштовано',
    } as Record<'settings' | 'env' | 'none', string>,
    smtpSecure: 'SSL/TLS (465); вимкнено — STARTTLS (587)',
    login: 'Логін (адреса скриньки)',
    password: 'Пароль',
    secretKept: 'Збережено. Порожнє поле — не змінювати.',
    fromName: 'Ім’я відправника',
    fromEmail: 'Адреса відправника',
    fromEmailHint: 'Порожньо — логін скриньки',
    testLogin: 'Перевірити вхід',
    testSend: (email: string) => `Надіслати тестовий лист на ${email}`,
    testHint: 'Перевірка працює зі збереженими налаштуваннями — спершу натисніть «Зберегти».',
    imapSecure: 'SSL/TLS (993); вимкнено — STARTTLS (143)',
    hosting: 'Корпоративна пошта на хостингу (adm.tools)',
    hostingHint:
      'Потрібно, щоб HR створював скриньки працівникам прямо з сайту. Токен — у кабінеті adm.tools → Обліковий запис → API; обмежте його IP-адресою сервера.',
    mailDomain: 'Поштовий домен',
    apiToken: 'Токен API',
    apiTokenHint: 'Не задано',
    domainLinked: (domain: string, id: number) =>
      `Домен ${domain} знайдено на хостингу (id ${id}).`,
    testApi: 'Перевірити доступ до API',
    webhook: 'Сповіщення про нові листи',
    webhookHint:
      'Хостинг повідомляє сайт про нові листи, і поштовик оновлюється одразу (без цього — раз на хвилину). Хостинг перевіряє сайт файлом webhook-confirm.txt — сайт віддає його сам.',
    accountId: 'ID акаунта хостингу',
    accountIdHint: 'Видно в шапці adm.tools праворуч («ID: …»). Спершу збережіть.',
    webhookOn: 'увімкнено',
    webhookOff: 'вимкнено',
    webhookEnable: 'Увімкнути сповіщення',
    webhookResync: 'Оновити список скриньок',
  },
  signed: {
    title: 'Підписаний примірник',
    upload: 'Завантажити підписаний',
    uploadNew: 'Завантажити нову версію',
    signedCount: (n: number) => (n === 1 ? 'Підписаний' : `Підписаний (версій: ${n})`),
    empty: 'Підписаний скан ще не завантажено.',
    latest: 'актуальний',
    signedOn: 'підписано',
    uploadedBy: (name: string, at: string) => `завантажив(ла) ${name}, ${at}`,
    view: 'Переглянути',
    download: 'Завантажити файл',
    remove: 'Видалити скан',
    removed: 'Скан видалено',
    files: 'Файли скана',
    filesHint: 'PDF або фото сторінок (JPG, PNG), до 15 МБ кожен',
    signedAt: 'Дата підписання',
    note: 'Примітка',
    actHint: 'Акт у статусі «чернетка» чи «видано» стане «підписаним».',
    submit: 'Завантажити',
    uploading: 'Завантаження…',
    uploaded: (n: number) => (n === 1 ? 'Скан завантажено' : `Завантажено файлів: ${n}`),
    failed: 'Не вдалося завантажити скан',
    unsupported: (name: string) => `${name}: потрібен PDF, JPG або PNG`,
    tooLarge: (name: string) => `${name}: файл завеликий (до 15 МБ)`,
    filter: 'Підписаний скан',
    filterAll: 'Усі',
    filterYes: 'Є скан',
    filterNo: 'Без скана',
  },
  invite: {
    sent: (email: string) => `Лист із запрошенням надіслано на ${email}`,
    notSent:
      'Лист не надіслано (пошта не налаштована або недоступна). Передайте посилання людині самостійно:',
    copy: 'Копіювати посилання',
    copied: 'Посилання скопійовано',
    title: 'Ласкаво просимо',
    subtitle: 'Задайте пароль для входу в систему',
    password: 'Новий пароль',
    confirm: 'Повторіть пароль',
    mismatch: 'Паролі не збігаються',
    submit: 'Зберегти пароль і увійти',
    submitting: 'Зберігаємо…',
    invalid: 'Посилання недійсне',
    invalidHint:
      'Посилання прострочене або вже використане. Попросіть адміністратора надіслати нове.',
    toLogin: 'Перейти до входу',
  },
  contests: {
    title: 'Конкурси',
    new: 'Новий конкурс',
    edit: 'Редагувати конкурс',
    position: 'Посада',
    location: 'Місто',
    project: 'Проєкт',
    projectHint: 'Наприклад, УВКБ ООН, FCDO PULSE, DoS-RCC',
    titleField: 'Назва',
    titleHint: 'Порожньо — збирається автоматично з посади, міста і проєкту',
    announcedAt: 'Оголошено',
    endsAt: 'Завершення',
    endsAtHint: 'Обчислюється: дата оголошення + 1 місяць',
    protocol2Date: 'Протокол №2',
    closedEarlyAt: 'Дострокове',
    closedEarlyHint: 'Заповнюється, якщо Протокол №2 раніше дати завершення',
    externalUrl: 'Посилання на вакансію',
    status: 'Статус',
    candidates: 'Кандидати',
    stages: 'Етапи',
    period: 'Період оголошення',
    from: 'з',
    to: 'по',
    created: 'Конкурс створено',
    empty: 'Конкурсів ще немає',
    emptyHint: 'Створіть перший конкурс — і додавайте в нього кандидатів.',
    emptyFiltered: 'За цими фільтрами конкурсів немає',
    resetFilters: 'Скинути фільтри',
    searchPlaceholder: 'Пошук за назвою…',
    exportExcel: 'Експорт кандидатів у Excel',
    exportList: 'Експорт в Excel',
    exportWord: 'Опис у Word',
    close: 'Закрити конкурс',
    closed: 'Конкурс закрито',
    reopen: 'Відкрити знову',
    tabCandidates: 'Кандидати',
    tabDescription: 'Опис',
    tabDetails: 'Конкурс',
    openVacancy: 'Вакансія на сайті',
    createdBy: 'Створив',
    newPosition: (title: string) => `Додати посаду «${title}»`,
  },
  candidates: {
    title: 'Кандидати',
    delete: 'Видалити кандидата',
    deleteTitle: (name: string) => `Видалити кандидата ${name}?`,
    deleteHint:
      'Буде видалено картку, резюме та інші документи, коментарі, а також участь у конкурсах з історією етапів і оцінками. Скасувати це не можна.',
    deleteContests: 'Кандидат зникне з конкурсів:',
    deleted: 'Кандидата видалено',
    fullName: 'ПІБ',
    lastName: 'Прізвище',
    firstName: "Ім'я",
    middleName: 'По батькові',
    phone: 'Телефон',
    phoneInvalid: 'Номер не розпізнано — збережеться як є',
    email: 'Email',
    city: 'Місто',
    notes: 'Нотатки',
    applications: 'Участі',
    createdAt: 'Додано',
    searchPlaceholder: 'Пошук за ПІБ або телефоном…',
    repeatOnly: 'Тільки повторні',
    repeat: (n: number) => `Повторний · ${n}`,
    repeatTitle: 'Участь у конкурсах',
    thisContest: 'цей конкурс',
    empty: 'Кандидатів не знайдено',
    emptyHint: 'Кандидати з’являються, коли їх додають у конкурс.',
    card: 'Картка кандидата',
    contacts: 'Контакти',
    participations: 'Участь у конкурсах',
    noParticipations: 'Ще не брав участі в конкурсах',
    history: 'Історія етапів',
    comments: 'Коментарі',
    openContest: 'Відкрити конкурс',
    saved: 'Дані кандидата збережено',
  },
  applications: {
    add: 'Додати кандидата',
    quickAddTitle: 'Новий кандидат у конкурсі',
    added: 'Кандидата додано в конкурс',
    source: 'Джерело',
    appliedAt: 'Дата відгуку',
    education: 'Освіта',
    experience: 'Досвід роботи',
    readyForFop: 'Готовність працювати як ФОП',
    militaryDocs: 'Військово-облікові документи',
    availableFrom: 'Коли готові приступити',
    recommendedPosition: 'Рекомендація по вакансії',
    stage: 'Етап',
    followUpAt: 'Нагадування',
    followUpHint: 'Коли перетелефонувати',
    comment: 'Коментар',
    lastComment: 'Останній коментар',
    addComment: 'Додати коментар',
    commentPlaceholder: 'Напишіть коментар…',
    winnerLetterSentAt: 'Лист переможцю',
    rejectionLetterSentAt: 'Лист відмови',
    startedAt: 'Вихід на роботу',
    contractRef: 'Наказ / договір',
    declinedReason: 'Причина відмови',
    details: 'Участь у конкурсі',
    history: 'Історія',
    remove: 'Прибрати з конкурсу',
    removed: 'Кандидата прибрано з конкурсу',
    viewTable: 'Таблиця',
    viewKanban: 'Канбан',
    empty: 'У конкурсі ще немає кандидатів',
    emptyHint: 'Додайте кандидата: ПІБ і телефон, решту можна заповнити пізніше.',
    stageChanged: (name: string, stage: string) => `${name}: ${stage}`,
    stageDialogTitle: 'Зміна етапу',
    backwardHint: 'Повернення на попередній етап — вкажіть причину.',
    hiredHint: 'Для виходу на роботу потрібні дата і номер наказу чи договору.',
    declinedHint: 'Можна вказати причину відмови.',
    undoComment: 'Скасування зміни етапу',
    moveTo: 'Перемістити на етап',
    open: 'Відкрити',
    filterStage: 'Етап',
    allStages: 'Усі етапи',
    events: {
      created: 'Додано в конкурс',
      stage: 'Етап',
      field: 'Змінено поле',
    },
  },
  stages: {
    title: 'Етапи',
    exitGroup: 'Завершення без найму',
    mainGroup: 'Основний шлях',
    mainHint: 'Нові кандидати потрапляють на перший етап. Порядок змінюйте стрілками.',
    exitHint: 'Етапи з дією «Відмова» чи «Вибуття» — участь завершено без працевлаштування.',
    name: 'Назва етапу',
    kind: 'Дія',
    kindNone: 'Без дії',
    color: 'Колір',
    add: 'Додати етап',
    addExit: 'Додати етап вибуття',
    remove: 'Видалити етап',
    moveUp: 'Вище',
    moveDown: 'Нижче',
    candidates: (n: number) => `кандидатів: ${n}`,
    busy: 'На етапі є кандидати — спершу перемістіть їх',
    save: 'Зберегти етапи',
    saved: 'Етапи збережено',
    makeDefault: 'Зробити стандартним',
    madeDefault: 'Набір збережено як стандартний — його отримуватимуть нові конкурси',
    resetToDefault: 'Взяти стандартний набір',
    defaultTitle: 'Стандартні етапи',
    defaultHint:
      'Цей набір отримує кожен новий конкурс. Наявні конкурси не змінюються — їхні етапи редагуються в самому конкурсі.',
    contestHint:
      'Етапи цього конкурсу. Зміни не зачіпають інші конкурси; щоб нові конкурси отримували такий самий набір — «Зробити стандартним».',
    unsaved: 'Є незбережені зміни',
  },
  comments: {
    empty: 'Коментарів ще немає',
    general: 'Про кандидата',
    removed: 'Коментар видалено',
  },
  resumes: {
    title: 'Резюме і документи',
    field: 'Резюме',
    forContest: 'Резюме, подане на цей конкурс',
    drop: 'Перетягніть резюме, мотиваційний лист чи інші документи сюди або натисніть, щоб обрати',
    dropShort: 'Резюме: перетягніть файл або натисніть',
    formats: 'PDF, DOC, DOCX, RTF, ODT, JPG, PNG · до 15 МБ',
    uploading: 'Завантаження…',
    uploaded: (n: number) => `Завантажено файлів: ${n}`,
    duplicate: (name: string) => `«${name}» уже є в системі — використано наявний файл`,
    unsupported: (name: string) => `«${name}»: непідтримуваний формат`,
    tooLarge: (name: string) => `«${name}»: файл більший за 15 МБ`,
    failed: 'Не вдалося завантажити файл',
    view: 'Переглянути',
    download: 'Завантажити',
    remove: 'Видалити резюме',
    removed: 'Резюме видалено',
    empty: 'Резюме ще не завантажено',
    parsedFilled: (fields: string) =>
      `З резюме підставлено: ${fields}. Перевірте перед збереженням.`,
    parsedNothing: 'У резюме не знайшлося ПІБ і контактів — заповніть поля вручну.',
    attached: 'Файл буде прив’язано до кандидата',
    belongsTo: 'Цей файл уже прикріплений до іншого кандидата',
  },
  preview: {
    title: 'Перегляд документа',
    loadFailed: 'Не вдалося отримати файл. Спробуйте ще раз або завантажте його.',
    failed: 'Не вдалося показати документ — завантажте файл, щоб відкрити його в Word чи Excel.',
    unsupported: 'Перегляд цього формату в браузері недоступний (DOC, RTF, ODT) — завантажте файл.',
    emptySheet: 'Аркуш порожній',
    truncated: (rows: number, cols: number) =>
      `Показано перші ${rows} рядків і ${cols} колонок — повністю документ видно після завантаження.`,
  },
  editor: {
    link: 'Посилання',
    linkPrompt: 'Адреса посилання (сайт або email)',
    placeholder: 'Опис вакансії: обов’язки, вимоги, умови…',
    saving: 'Збереження…',
    saved: 'Збережено',
    unsaved: 'Є незбережені зміни',
    bold: 'Жирний',
    italic: 'Курсив',
    underline: 'Підкреслений',
    heading: 'Заголовок',
    subheading: 'Підзаголовок',
    bulletList: 'Маркований список',
    orderedList: 'Нумерований список',
    undo: 'Скасувати дію',
    redo: 'Повторити дію',
    toolbar: 'Форматування',
  },
  merge: {
    action: 'Злити',
    title: 'Злиття кандидатів',
    description:
      'Участі в конкурсах, резюме й коментарі перейдуть до кандидата, якого ви залишаєте. Порожні контакти доповняться. Другий запис буде видалено.',
    keep: 'Залишити цього кандидата',
    confirm: 'Злити кандидатів',
    done: 'Кандидатів злито',
    adminOnly: 'Зливати кандидатів може адміністратор',
  },
  kanban: {
    instructions:
      'Щоб перемістити картку, натисніть пробіл або Enter, стрілками оберіть місце і знову натисніть пробіл. Escape — скасувати.',
    picked: (name: string) => `Взято картку ${name}`,
    over: (stage: string) => `Над колонкою «${stage}»`,
    dropped: (stage: string) => `Переміщено в «${stage}»`,
    cancelled: 'Переміщення скасовано',
  },
  duplicates: {
    title: 'Можливі дублікати',
    found: 'Схожий кандидат уже є',
    foundHint: 'Перевірте, чи це не та сама людина. Однакові номери трапляються і в різних людей.',
    same: 'Це та сама людина',
    different: 'Інша людина',
    usingExisting: (name: string) => `Буде додано наявного кандидата: ${name}`,
    markedDifferent: 'Позначено як інша людина',
    checking: 'Перевіряємо дублі…',
    editHint:
      'Після збереження пара з’явиться на екрані «Можливі дублікати», де її можна злити або позначити як різних людей.',
    alreadyInContest: 'Уже бере участь у цьому конкурсі',
    resolveFirst: 'Спершу вирішіть, чи це та сама людина',
    undoChoice: 'Змінити вибір',
    reason: 'Причина',
    dismiss: 'Різні люди',
    dismissed: 'Позначку закрито',
    empty: 'Можливих дублікатів немає',
    emptyHint: 'Система позначає пари кандидатів зі спільним телефоном, email або схожим ПІБ.',
    open: 'Відкриті',
    dismissedTab: 'Закриті',
    score: 'Схожість',
  },
  reminders: {
    title: 'Нагадування',
    description: 'Що зробити з конкурсами й кандидатами — по днях і годинах',
    contests: 'Конкурси',
    candidates: 'Кандидати',
    mine: 'Мої',
    all: 'Усієї команди',
    active: 'Заплановані',
    done: 'Виконані',
    buckets: {
      overdue: 'Прострочені',
      today: 'Сьогодні',
      tomorrow: 'Завтра',
      week: 'Найближчі 7 днів',
      later: 'Пізніше',
    },
    emptyActive: 'Нічого не заплановано',
    emptyDone: 'Виконаних нагадувань ще немає',
    emptyContestHint:
      'Нові конкурси автоматично отримують нагадування на 3, 7 і 13 день від оголошення. Додати своє — на сторінці конкурсу, кнопка «Нагадування».',
    emptyCandidateHint:
      'Нагадування про кандидата ставиться в його картці участі в конкурсі або в таблиці кандидатів (колонка «Нагадування»).',
    add: 'Нагадування',
    addNew: 'Додати нагадування',
    text: 'Що зробити',
    date: 'Дата',
    time: 'Час',
    quick: 'Швидко',
    inDays: (n: number) => (n === 0 ? 'сьогодні' : n === 1 ? 'завтра' : `через ${n} дн.`),
    created: 'Нагадування додано',
    saved: 'Нагадування змінено',
    markDone: 'Виконано',
    markUndone: 'Повернути в заплановані',
    doneToast: 'Нагадування виконано',
    removed: 'Нагадування видалено',
    edit: 'Змінити',
    remove: 'Видалити',
    auto: 'авто',
    autoHint: 'Створено автоматично за шаблоном конкурсу',
    for: (name: string) => `для: ${name}`,
    doneAt: (when: string) => `виконано ${when}`,
    none: 'Нагадувань немає',
    openContest: 'Відкрити конкурс',
    at: (date: string, time: string) => `${date}, ${time}`,
    contestSheetHint:
      'Автоматичні нагадування на 3, 7 і 13 день від оголошення отримує автор конкурсу. Додане тут нагадування — для вас.',
    settingsTitle: 'Нагадування',
    settingsHint:
      'Налаштування спільні для всіх HR. Зміни діють на нові конкурси й нові нагадування; уже створені не змінюються.',
    contestPresets: 'Автоматичні нагадування нового конкурсу',
    contestPresetsHint:
      'Створюються для того, хто створив конкурс. Дні — від дати оголошення; якщо дата вже минула, нагадування не створюється.',
    candidatePresets: 'Швидкі нагадування про кандидата',
    candidatePresetsHint: 'Кнопки у формі нагадування. Дні — від сьогодні.',
    manualDefaults: 'Нове нагадування вручну — за замовчуванням',
    days: 'Днів',
    addPreset: 'Додати шаблон',
    removePreset: 'Видалити шаблон',
    contestManual: 'Про конкурс',
    candidateManual: 'Про кандидата',
  },
  reference: {
    title: 'Довідники',
    positions: 'Посади',
    sources: 'Джерела',
    locations: 'Міста',
    name: 'Назва',
    kind: 'Тип',
    archived: 'В архіві',
    showArchived: 'Показати архівні',
    add: 'Додати',
    adminOnly: 'Редагувати довідники може адміністратор.',
    renameWarning: (n: number) => `Запис використано ${n} раз(и) — нова назва з’явиться всюди.`,
  },
  search: {
    candidates: 'Кандидати',
    contests: 'Конкурси',
    minChars: 'Введіть щонайменше 2 символи',
    navigation: 'Перехід',
  },
  conflict: {
    remoteChanged:
      'Цей запис щойно змінив колега. Ваші незбережені зміни можуть перезаписати його.',
    reload: 'Завантажити зміни колеги',
  },
  notFound: {
    title: 'Сторінку не знайдено',
    back: 'На головну',
  },
} as const;
